import {
  ActivateOrg,
  ApiRoute,
  CreateOrg,
  CreateOrgReview,
  Org,
  OrgActivationStatus,
  OrgActivationStatusSchema,
  OrgFullDetail,
  OrgFullDetailSchema,
  OrgInvitation,
  OrgInvitationDetail,
  OrgInvitationDetailSchema,
  OrgInvitationSchema,
  OrgMemberDetail,
  OrgMemberDetailSchema,
  OrgMemberRole,
  OrgPublicDetail,
  OrgPublicDetailSchema,
  OrgReview,
  OrgReviewDetail,
  OrgReviewDetailSchema,
  OrgReviewSchema,
  OrgSchema,
  SendOrgInvitationRequest,
  UpdateOrg,
  getApiRoute
} from "@humanex/interfaces";
import { z } from "zod";

import { ApiClientConfig, BaseApiClient } from "./base-client";

export class OrgApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({
      ...config,
      baseEndpoint: getApiRoute(ApiRoute.ORG)
    });
  }

  async create(data: CreateOrg): Promise<OrgFullDetail> {
    return await this.post("", OrgFullDetailSchema, data, {
      requireAuth: true
    });
  }

  async getListByUserId(userId: string): Promise<OrgMemberDetail[]> {
    return await this.get(`/user/${userId}`, z.array(OrgMemberDetailSchema), {
      requireAuth: true
    });
  }

  async getListPopular(): Promise<Org[]> {
    return await this.get(`/popular`, z.array(OrgSchema), {
      requireAuth: false
    });
  }

  async getOne(orgId: string): Promise<OrgPublicDetail | null> {
    try {
      return await this.get(orgId, OrgPublicDetailSchema, {
        requireAuth: false
      });
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async getFullDetail(orgId: string): Promise<OrgFullDetail | null> {
    try {
      return await this.get(`${orgId}/full`, OrgFullDetailSchema, {
        requireAuth: false
      });
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async getReviews(orgId: string): Promise<OrgReviewDetail[]> {
    return await this.get(`${orgId}/reviews`, z.array(OrgReviewDetailSchema), {
      requireAuth: false
    });
  }

  async createReview(orgId: string, data: CreateOrgReview): Promise<OrgReview> {
    return await this.post(`${orgId}/reviews`, OrgReviewSchema, data, {
      requireAuth: true
    });
  }

  async update(orgId: string, data: UpdateOrg): Promise<OrgFullDetail> {
    return await this.patch(orgId, OrgFullDetailSchema, data, {
      requireAuth: true
    });
  }

  async updateMemberRole(orgId: string, userId: string, role: OrgMemberRole): Promise<void> {
    return await this.patch(
      `${orgId}/members/${userId}`,
      z.void(),
      { role },
      {
        requireAuth: true
      }
    );
  }

  async removeOrgMember(orgId: string, userId: string): Promise<void> {
    return await this.delete(`${orgId}/members/${userId}`, z.void(), {
      requireAuth: true
    }).then(() => undefined);
  }

  async getPendingInvitations(orgId: string): Promise<OrgInvitation[]> {
    return await this.get(`${orgId}/invitations`, z.array(OrgInvitationSchema), {
      requireAuth: true
    });
  }

  async sendInvitation(orgId: string, data: SendOrgInvitationRequest): Promise<void> {
    return await this.post(`${orgId}/invitations`, z.void(), data, {
      requireAuth: true
    });
  }

  async getOrgInvitation(token: string): Promise<OrgInvitationDetail> {
    return await this.get(`invitations/${token}`, OrgInvitationDetailSchema, {
      requireAuth: false
    });
  }

  async acceptOrgInvitation(token: string): Promise<void> {
    return await this.post(`invitations/${token}/accept`, z.void(), undefined, {
      requireAuth: true
    });
  }

  async getActivationStatus(orgId: string): Promise<OrgActivationStatus> {
    return await this.get(`${orgId}/activation`, OrgActivationStatusSchema, { requireAuth: false });
  }

  async activate(orgId: string, data: ActivateOrg): Promise<void> {
    return await this.post(`${orgId}/activate`, z.void(), data, { requireAuth: true });
  }
}
